// Política de privacidad de la tienda (/privacidad): qué datos guarda, para qué, con quién se comparten y cómo pedir la
// baja, con el nombre de la marca. Es la dirección que se anota en Google, Apple y Facebook (decisiones D48 y D51), por
// eso se ve sin cuenta y aunque la tienda esté cerrada. Facebook pide además las instrucciones para borrar los datos:
// la sección "Cómo borrar tus datos" (/privacidad#borrar-datos). Abajo, los créditos: los de las fotos que no son de
// la distribuidora (Configuración → Tienda → Créditos de las fotos) y los del mapa, los íconos y la letra.
import { cx, Icono } from '@reparto/ui';
import { Fragment, type ReactNode, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { Pantalla } from '../componentes/Marco';
import { api } from '../datos/api';
import { useConsulta } from '../datos/consultas';
import { useMarca } from '../datos/marca';
import { SLUG } from '../datos/tienda-actual';

interface DatosPrivacidad {
  nombre: string;
  contacto: string;
  creditos_fotos: string;
}

/** Fecha de este texto: cambia cuando cambia lo que dice. */
const ACTUALIZADA = '20 de octubre de 2026';

const DIRECCION = /(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"])/;

/** Un renglón de texto con sus direcciones como enlaces. */
export function ConEnlaces({ texto }: { texto: string }) {
  return (
    <>
      {texto.split(DIRECCION).map((parte, i) =>
        i % 2 ? (
          <a
            key={i}
            href={parte}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-primary-700 underline underline-offset-2"
          >
            {parte.replace(/^https?:\/\//, '')}
          </a>
        ) : (
          <Fragment key={i}>{parte}</Fragment>
        ),
      )}
    </>
  );
}

/** "Privacidad y créditos": al pie del inicio, en la entrada y en la cuenta. */
export function EnlacePrivacidad({ className }: { className?: string }) {
  return (
    <Link
      to="/privacidad"
      className={cx(
        'inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 text-[15px] font-medium text-stone-700 hover:text-stone-900',
        'focus-visible:outline-2 focus-visible:outline-primary-600',
        className,
      )}
    >
      <Icono nombre="shield" tamano={20} className="text-stone-600" />
      <span className="underline underline-offset-2">Privacidad y créditos</span>
    </Link>
  );
}

export function Privacidad() {
  const marca = useMarca((s) => s.marca);
  const c = useConsulta<DatosPrivacidad>('privacidad', () =>
    api<DatosPrivacidad>('/tienda/publico/privacidad', { consulta: { slug: SLUG }, sinSesion: true }),
  );
  const nombre = c.datos?.nombre ?? marca?.nombre ?? 'la distribuidora';
  // Con /privacidad#borrar-datos (la dirección que se anota en Facebook) va directo a esa sección.
  const { hash } = useLocation();
  const cargada = !!c.datos;
  useEffect(() => {
    if (!hash || hash.length > 40) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: 'start' });
  }, [hash, cargada]);
  const contacto = c.datos?.contacto ?? '';
  const creditos = (c.datos?.creditos_fotos ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <Pantalla titulo="Política de privacidad" subtitulo={`Tienda de ${nombre}`}>
      <div className="px-4 pt-3 pb-10 text-[17px] leading-normal text-stone-800">
        <p>
          Esta es la tienda donde los clientes de <b className="font-semibold text-stone-900">{nombre}</b> hacen sus
          pedidos. Acá te contamos qué datos guarda, para qué los usa, con quién se comparten y cómo pedir la baja.
        </p>
        <p className="mt-2 text-[15px] text-stone-600">Actualizada el {ACTUALIZADA}.</p>

        <Seccion icono="badge" titulo="Qué datos guarda la tienda">
          <ul className="flex list-disc flex-col gap-1.5 pl-5 marker:text-stone-500">
            <li>
              Tu correo y tu contraseña, para entrar. La contraseña se guarda cifrada: nadie la puede leer, ni la
              distribuidora.
            </li>
            <li>Tu nombre, el nombre de tu negocio, su celular o WhatsApp y, si lo cargás, el RUC o la CI.</li>
            <li>La dirección, la referencia y la ubicación del negocio en el mapa, para que el pedido llegue.</li>
            <li>
              Si entrás con Google, Apple o Facebook: tu nombre, tu correo y el identificador de tu cuenta en ese
              servicio (con Apple, el correo puede ser una dirección de reenvío que Apple arma para vos). Nunca tu
              contraseña de esos servicios ni nada más de tu cuenta.
            </li>
            <li>Si la distribuidora lo prendió, tu número de celular para entrar con un código.</li>
            <li>Tus pedidos: qué pedís, para cuándo, cómo pagás y lo que nos avisás.</li>
            <li>
              En tu celular quedan la sesión, el carrito y el modo claro u oscuro, para no empezar de cero cada vez.
            </li>
          </ul>
        </Seccion>

        <Seccion icono="local_shipping" titulo="Para qué">
          <p>
            Solo para tomar tus pedidos y entregarlos: mostrarte tus precios y lo que pedís siempre, preparar la carga,
            armar el recorrido del chofer, cobrar al entregar y avisarte cómo va cada pedido. No se usan para
            publicidad.
          </p>
        </Seccion>

        <Seccion icono="group" titulo="Con quién se comparten">
          <p>
            Con <b className="font-semibold text-stone-900">{nombre}</b>, la distribuidora de esta tienda: su personal
            ve tus datos y tus pedidos para prepararlos, y el chofer que te lleva el pedido ve el nombre, la dirección,
            la ubicación y el teléfono del negocio. No se comparten con otras empresas. Si pedís un enlace para tu
            contraseña, el correo sale por el servidor de correo de la tienda; si el código te llega por SMS, tu número
            pasa por el servicio que manda el mensaje.
          </p>
        </Seccion>

        <Seccion icono="person_remove" titulo="Cómo pedir la baja">
          <p>
            Cuando quieras, podés pedir que te demos de baja, que corrijamos tus datos o que los borremos:{' '}
            {contacto ? (
              <>
                escribí a <b className="font-semibold break-all text-stone-900">{contacto}</b>
              </>
            ) : (
              <>avisale a {nombre} por donde hacés tus pedidos (WhatsApp, teléfono o tu vendedor)</>
            )}
            . Se guarda solo lo que haya que conservar por ley, como lo facturado.
          </p>
        </Seccion>

        <Seccion id="borrar-datos" icono="delete" titulo="Cómo borrar tus datos">
          <ol className="flex list-decimal flex-col gap-2 pl-5 marker:font-semibold marker:text-stone-700">
            <li>
              Pedí que borremos tu cuenta y tus datos:{' '}
              {contacto ? (
                <>
                  escribí a <b className="font-semibold break-all text-stone-900">{contacto}</b>
                </>
              ) : (
                <>avisale a {nombre} por donde hacés tus pedidos</>
              )}{' '}
              con el correo de tu cuenta (o el nombre de tu negocio). En hasta 30 días borramos tu cuenta, tu
              contraseña, los datos del negocio y el vínculo con Google, Apple o Facebook, y te avisamos. Lo facturado
              se guarda el tiempo que pide la ley.
            </li>
            <li>
              Si entraste con Facebook, quitá la tienda de tu cuenta: en Facebook, Configuración y privacidad →
              Configuración → Integraciones comerciales (o «Apps y sitios web») → elegí la tienda → Eliminar. Más
              detalles: <ConEnlaces texto="https://www.facebook.com/help/170585223002660" />.
            </li>
            <li>
              Si entraste con Apple: en el iPhone, Configuración → tu nombre → Iniciar sesión con Apple → elegí la
              tienda → Dejar de usar el ID de Apple; o en <ConEnlaces texto="https://account.apple.com" />.
            </li>
            <li>
              Si entraste con Google: <ConEnlaces texto="https://myaccount.google.com/connections" />, elegí la tienda y
              quitale el acceso.
            </li>
          </ol>
        </Seccion>

        <Seccion icono="photo_library" titulo="Créditos">
          {creditos.length ? (
            <>
              <p>Algunas fotos de los productos no son de {nombre}: son de terceros, con licencia libre.</p>
              <ul className="mt-2 flex flex-col gap-2 text-[15px] leading-snug text-stone-700">
                {creditos.map((l, i) => (
                  <li key={i} className="break-words">
                    <ConEnlaces texto={l} />
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <ul className={creditos.length ? 'mt-3 flex flex-col gap-1 text-[15px]' : 'flex flex-col gap-1 text-[15px]'}>
            <li>
              Mapa: © colaboradores de OpenStreetMap (ODbL),{' '}
              <ConEnlaces texto="https://www.openstreetmap.org/copyright" />.
            </li>
            <li>Íconos: Material Symbols de Google (licencia Apache 2.0).</li>
            <li>Letra: Roboto (SIL Open Font License).</li>
          </ul>
        </Seccion>

        <Link
          to="/"
          className="mt-8 inline-flex min-h-11 items-center gap-1 font-semibold text-primary-700 hover:underline"
        >
          <Icono nombre="arrow_back" tamano={20} />
          Volver a la tienda
        </Link>
      </div>
    </Pantalla>
  );
}

function Seccion({ id, icono, titulo, children }: { id?: string; icono: string; titulo: string; children: ReactNode }) {
  return (
    <section id={id} className="mt-7 scroll-mt-24">
      <h2 className="mb-2 flex items-center gap-2 text-xl font-bold text-stone-900">
        <Icono nombre={icono} tamano={24} className="text-primary-700" />
        {titulo}
      </h2>
      {children}
    </section>
  );
}
